import {BaseFlagProps, Parameter} from 'platform/components';
import {formatPhoneNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {always, isNil, isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {useGetBusinessCaseQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {useGetSaleVehicleFromSaleVehicleIdQuery} from '@dms/api/vehicle';
import {CustomerInsightsTrigger} from '@dms/features/customer-insights';
import i18n from '@dms/i18n';
import {
  businessCaseRoutes,
  customerRoutes,
  interestRoutes,
  testIds,
  vehiclesRoutes,
} from '@dms/routes';
import {
  EMPTY_PLACEHOLDER,
  getCustomerEmail,
  getCustomerName,
  getCustomerPhoneNumber,
  getEmailParameter,
  getLocaleLabel,
  getNaturalPersonFullName,
  SnippetProps,
  useGetFlags,
  useGetVehicleTitle,
  usePermissions,
  useTenant,
} from '@dms/shared';

import {buildArray, composePath, Nullish, useNavigate} from 'shared/web';

import {getLastActiveBusinessCase} from '../utils/getLastActiveBusinessCase';
import {useInterestData} from './useInterestData';

// eslint-disable-next-line complexity -- Pre-existing violation
export function useInterestSnippets(interestId: string) {
  const navigate = useNavigate();

  const {isMultiLocale} = useTenant();
  const {interest, customer, miniSale, offeredVehiclesList} = useInterestData(interestId);

  const contactPerson = isNotNil(interest?.contactPersonId)
    ? customer?.contacts?.find((person) => person.id === interest?.contactPersonId)
    : undefined;

  const customerSnippetTitle = isNotNil(contactPerson)
    ? getNaturalPersonFullName(contactPerson)
    : getCustomerName(customer);

  const contactPersonPhoneNumber = contactPerson?.phoneNumbers?.[0];
  const contactPersonEmail = contactPerson?.emails?.[0]?.email;
  const formattedContactPersonPhoneNumber =
    isNotNil(contactPersonPhoneNumber) &&
    isNotNilOrEmpty(contactPersonPhoneNumber.prefix) &&
    isNotNilOrEmpty(contactPersonPhoneNumber.number)
      ? formatPhoneNumber(
          `${contactPersonPhoneNumber.prefix}${contactPersonPhoneNumber.number}`,
          contactPersonPhoneNumber.countryCode
        )
      : null;

  const lastActiveBusinessCase = getLastActiveBusinessCase(interest?.activeBusinessCases);
  const {data: businessCase} = useGetBusinessCaseQuery(
    {businessCaseId: lastActiveBusinessCase?.businessCaseId ?? ''},
    {skip: isNilOrEmpty(lastActiveBusinessCase?.businessCaseId)}
  );
  const {data: saleVehicle} = useGetSaleVehicleFromSaleVehicleIdQuery(
    {vehicleId: businessCase?.saleVehicleId ?? ''},
    {skip: isNilOrEmpty(businessCase?.saleVehicleId)}
  );
  const [flags] = useGetFlags(businessCase, saleVehicle);

  const [buyingVehicleTitle] = useGetVehicleTitle({
    make: miniSale?.vehicleData?.make,
    vehicleType: miniSale?.vehicleData?.type?.toString(),
    modelFamily: miniSale?.vehicleData?.modelFamily,
    variant: miniSale?.vehicleData?.variant,
    trim: miniSale?.vehicleData?.trim,
  });
  const [sellingVehicleTitle] = useGetVehicleTitle(offeredVehiclesList?.[0]);
  const [businessCaseVehicleTitle] = useGetVehicleTitle(lastActiveBusinessCase?.vehicleInfo);

  const {data: businessCaseParticipation} = useGetParticipationQuery(
    {
      recordId: lastActiveBusinessCase?.businessCaseId ?? '',
      resourceId: EntityResourceIds.businessCase,
    },
    {skip: isNilOrEmpty(lastActiveBusinessCase?.businessCaseId)}
  );

  const [canReadSaleBusinessCase, canReadPurchaseBusinessCase] = usePermissions({
    permissionKeys: ['viewBusinessCaseSelling', 'viewBusinessCasePurchase'],
    scopes: {
      viewBusinessCaseSelling: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
      viewBusinessCasePurchase: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
    },
  });

  const canViewLastActiveBusinessCase = match<string | Nullish, boolean>(interest?.type)
    .with('SELLING', always(canReadSaleBusinessCase))
    .with('BUYING', always(canReadPurchaseBusinessCase))
    .otherwise(always(true));

  return buildArray<SnippetProps>()
    .add({
      'data-testid': testIds.interest.detail('customer'),
      resourceId: EntityResourceIds.interest,
      recordId: interestId,
      label: i18n.t('entity.customer.labels.customer'),
      placeholder: {
        title: i18n.t('entity.customer.labels.customer'),
      },
      title: customerSnippetTitle ?? EMPTY_PLACEHOLDER,
      action: (
        <CustomerInsightsTrigger
          customerId={customer?.id}
          data-testid="interest-header-customerInsights"
        />
      ),
      flags: buildArray<BaseFlagProps>().when(
        isMultiLocale && isNotNil(customer?.preferredLocale),
        {
          colorScheme: 'neutral',
          isSubtle: true,
          label: getLocaleLabel(customer?.preferredLocale) ?? '',
        }
      ),
      parameters: buildArray<Parameter>()
        .when(
          isNotNil(contactPerson) && isNotNil(formattedContactPersonPhoneNumber),
          formattedContactPersonPhoneNumber
        )
        .when(
          isNotNil(contactPerson) && isNotNilOrEmpty(contactPersonEmail),
          getEmailParameter(contactPersonEmail)
        )
        .when(isNil(contactPerson) && isNotNil(customer), getCustomerPhoneNumber(customer))
        .when(
          isNil(contactPerson) && isNotNil(customer),
          getEmailParameter(getCustomerEmail(customer))
        ),
      href: customer?.id
        ? composePath(customerRoutes.detail, {
            params: {id: customer?.id},
            isSearchParamsPreserved: false,
          })
        : undefined,
      customerId: customer?.id,
      selectedContactId: contactPerson?.id ?? customer?.foundingPerson?.id,
    })
    .add({
      'data-testid': testIds.interest.detail('selling'),
      label: i18n.t('entity.interest.labels.sellingTab'),
      placeholder: {
        title: i18n.t('entity.interest.actions.addSellingRequest'),
        onClick: () =>
          navigate(composePath(interestRoutes.detailSelling, {params: {id: interestId}})),
      },
      title: sellingVehicleTitle ?? EMPTY_PLACEHOLDER,
      parameters: [
        offeredVehiclesList?.[0]?.vin ?? i18n.t('entity.vehicle.labels.noVin'),
        offeredVehiclesList?.[0]?.vehicle?.registrationPlate ??
          i18n.t('entity.vehicle.notifications.noLicencePlate'),
      ],
      href: offeredVehiclesList?.[0]
        ? composePath(vehiclesRoutes.detail, {
            params: {id: offeredVehiclesList?.[0]?.id},
            isSearchParamsPreserved: false,
          })
        : undefined,
    })
    .add({
      'data-testid': testIds.interest.detail('buying'),
      label: i18n.t('entity.interest.labels.buyingTab'),
      placeholder: {
        title: i18n.t('entity.interest.actions.addBuyingRequest'),
        onClick: () =>
          navigate(composePath(interestRoutes.detailBuying, {params: {id: interestId}})),
      },
      title: buyingVehicleTitle ?? EMPTY_PLACEHOLDER,
      parameters: [
        miniSale?.vehicleData?.vin ?? i18n.t('entity.vehicle.labels.noVin'),
        miniSale?.vehicleData?.licencePlate ??
          i18n.t('entity.vehicle.notifications.noLicencePlate'),
      ],
    })
    .when(isNotNil(lastActiveBusinessCase) && canViewLastActiveBusinessCase, {
      flags,
      'data-testid': testIds.interest.detail('activeBusinessCase'),
      label: i18n.t('entity.interest.labels.activeBusinessCase'),
      title: businessCaseVehicleTitle ?? EMPTY_PLACEHOLDER,
      parameters: buildArray<Parameter>()
        .when(lastActiveBusinessCase?.code, lastActiveBusinessCase?.code)
        .when(
          lastActiveBusinessCase?.ownerFirstName && lastActiveBusinessCase?.ownerLastName,
          `${lastActiveBusinessCase?.ownerFirstName} ${lastActiveBusinessCase?.ownerLastName}`
        ),
      icon: 'places/business_center',
      href: lastActiveBusinessCase?.businessCaseId
        ? composePath(businessCaseRoutes.overview, {
            params: {id: lastActiveBusinessCase?.businessCaseId},
            isSearchParamsPreserved: false,
          })
        : undefined,
    });
}
